import cx from 'clsx';
import React, { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { ReactComponent as TableCollapseIcon } from '@assets/svg/ic-tableCollapse.svg';
import { ReactComponent as ThreeDotsIcon } from '@assets/svg/three-dots.svg';

import TableSorting from '@components/common/TableSorting/TableSorting';
import TooltipSort from '@components/common/TooltipSort/TooltipSort';
import Button from '@components/uikit/Button/Button';

import { useDebouncedValue } from '@hooks/useDebouncedValue';
import useTranslation from '@hooks/useTranslation';

import type { ITableSorting } from '@model/tableSorting';

import { useFacilityGroupsQuery } from '@services/useFacilityGroupsQuery';

import { getSortedData } from '../../helpers/getSortedData';
import {
  selectGroupSorting,
  selectSearchGroup,
  useManageFacilityStoreActions,
} from '../../store/useManageFacilityStore';
import ContextMenu from '../ContextMenu/ContextMenu';
import EmptyFilterBlock from '../EmptyFilterBlock/EmptyFilterBlock';
import NestedTableFacilityGroup from '../NestedTableFacilityGroup/NestedTableFacilityGroup';
import TableFacilityGroupsSkeleton from './TableFacilityGroupsSkeleton';
import style from './style.module.scss';

const GROUPS_TABLE_SORT_TOOLTIP_ID = 'groups-table-sort-tooltip-id';
const TABLE_GROUPS_ACTION_MENU_ID = 'table-facility-groups-action-menu';

const TableFacilityGroups = () => {
  const { t } = useTranslation();

  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());

  const { setGroupSorting } = useManageFacilityStoreActions();
  const searchGroup = selectSearchGroup();
  const groupSorting: ITableSorting = selectGroupSorting();

  const [debouncedValue] = useDebouncedValue(searchGroup, 500);

  const { data: facilityGroupsData, isLoading: isFacilityGroupsLoading } = useFacilityGroupsQuery({
    search: debouncedValue,
  });

  useEffect(() => {
    if (facilityGroupsData?.data?.length) {
      setExpandedGroups(new Set(facilityGroupsData?.data?.map((item) => item.id)));
    }
  }, [facilityGroupsData]);

  const tableData = getSortedData(groupSorting, facilityGroupsData?.data);
  const isExpandedAll = expandedGroups.size === tableData.length;

  const toggleGroupRow = (type: number) => {
    setExpandedGroups((prevExpandedGroup) => {
      const newSelectedGroupRows = new Set(prevExpandedGroup);
      if (newSelectedGroupRows.has(type)) {
        newSelectedGroupRows.delete(type);
      } else {
        newSelectedGroupRows.add(type);
      }
      return newSelectedGroupRows;
    });
  };

  const handleCollapseAll = () => setExpandedGroups(new Set());
  const handleExpandAll = () => setExpandedGroups(new Set(tableData.map((item) => item.id)));

  return (
    <div className={style.tableWrapper}>
      <table className={style.facilityGroupsTable}>
        <thead className={style.facilityGroupsTableHeader}>
          <tr>
            <th className={style.thWithDetails}>
              {t('Facility Group')}
              <TableSorting
                sortingKey="name"
                sorting={groupSorting}
                setSorting={setGroupSorting}
                tooltipId={GROUPS_TABLE_SORT_TOOLTIP_ID}
              />
            </th>
            <th className={style.actionColumn}>
              {!!tableData?.length && (
                <Button
                  type="secondary"
                  text={isExpandedAll ? t('Collapse all') : t('Expand all')}
                  onClick={() => (isExpandedAll ? handleCollapseAll() : handleExpandAll())}
                />
              )}
            </th>
          </tr>
        </thead>
        <tbody>
          {isFacilityGroupsLoading ? (
            <TableFacilityGroupsSkeleton />
          ) : tableData.length ? (
            tableData.map((group) => (
              <React.Fragment key={uuidv4()}>
                <tr className={style.trWithDetails}>
                  <td className={style.tdWithDetails} onClick={() => toggleGroupRow(group.id)}>
                    <TableCollapseIcon
                      className={cx(style.arrowDetailsIcon, {
                        [style.arrowTop]: !!expandedGroups.has(group.id),
                      })}
                    />
                    {group.name} - {group.children.length}
                  </td>
                  <td className={style.actionColumn}>
                    <div
                      className={style.actionsIconContainer}
                      data-tooltip-id={TABLE_GROUPS_ACTION_MENU_ID}
                      data-tooltip-content={JSON.stringify(group)}
                    >
                      <ThreeDotsIcon />
                    </div>
                  </td>
                </tr>
                {expandedGroups.has(group.id) && (
                  <>
                    <tr className={style.detailsRow}>
                      <td colSpan={3}>
                        <NestedTableFacilityGroup facilityGroupId={group.id} facilityList={group.children} />
                      </td>
                    </tr>
                  </>
                )}
              </React.Fragment>
            ))
          ) : (
            <tr className={style.emptyRow}>
              <td colSpan={3}>
                <EmptyFilterBlock
                  mainText={t('No facility groups found')}
                  extraText={t('Create groups and assign facilities to view them here')}
                />
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <ContextMenu id={TABLE_GROUPS_ACTION_MENU_ID} />
      <TooltipSort id={GROUPS_TABLE_SORT_TOOLTIP_ID} sorting={groupSorting} />
    </div>
  );
};

export default TableFacilityGroups;
